/**
 * plugins/vuetify.ts
 *
 * Collector custom theme configuration
 * UX Spec: Museum-gallery aesthetic with Space Grotesk typography
 */

// Styles
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'

// Composables
import { createVuetify, type ThemeDefinition } from 'vuetify'

/**
 * Light theme - UX Spec colors
 * Background: Pure white #FFFFFF
 * Surface: Off-white #FAFAFA
 * Primary text: Charcoal #1A1A1A
 * Secondary text: Medium grey #6B6B6B
 * Borders: Light grey #E5E5E5
 * Accent: Soft black #333333
 */
const lightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    background: '#FFFFFF',
    surface: '#FAFAFA',
    'surface-variant': '#F5F5F5',
    'on-background': '#1A1A1A',
    'on-surface': '#1A1A1A',
    'on-surface-variant': '#6B6B6B',
    primary: '#333333',
    'on-primary': '#FFFFFF',
    secondary: '#6B6B6B',
    'on-secondary': '#FFFFFF',
    error: '#B00020',
    'on-error': '#FFFFFF',
    info: '#333333',
    success: '#1A1A1A',
    warning: '#6B6B6B',
  },
  variables: {
    'border-color': '#E5E5E5',
    'border-opacity': 1,
    'high-emphasis-opacity': 1,
    'medium-emphasis-opacity': 0.7,
    'disabled-opacity': 0.38,
  },
}

/**
 * Dark theme - UX Spec colors
 * Background: Near black #0A0A0A
 * Surface: Dark grey #1A1A1A
 * Primary text: Off-white #F5F5F5
 * Secondary text: Medium grey #9A9A9A
 * Borders: Dark grey #2A2A2A
 * Accent: Soft white #E0E0E0
 */
const darkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#0A0A0A',
    surface: '#1A1A1A',
    'surface-variant': '#2A2A2A',
    'on-background': '#F5F5F5',
    'on-surface': '#F5F5F5',
    'on-surface-variant': '#9A9A9A',
    primary: '#E0E0E0',
    'on-primary': '#0A0A0A',
    secondary: '#9A9A9A',
    'on-secondary': '#0A0A0A',
    error: '#CF6679',
    'on-error': '#000000',
    info: '#E0E0E0',
    success: '#F5F5F5',
    warning: '#9A9A9A',
  },
  variables: {
    'border-color': '#2A2A2A',
    'border-opacity': 1,
    'high-emphasis-opacity': 1,
    'medium-emphasis-opacity': 0.7,
    'disabled-opacity': 0.38,
  },
}

export default createVuetify({
  theme: {
    defaultTheme: 'light',
    themes: {
      light: lightTheme,
      dark: darkTheme,
    },
  },
  defaults: {
    global: {
      ripple: false, // UX Spec: Disabled (calm, not playful)
    },
    VApp: {
      style: {
        fontFamily: "'Space Grotesk', sans-serif",
      },
    },
    VCard: {
      rounded: 0, // UX Spec: Sharp corners (0px) - museum frame aesthetic
      elevation: 0, // UX Spec: Minimal/none - let images breathe
    },
    VBtn: {
      rounded: 0, // UX Spec: Sharp corners
      elevation: 0, // UX Spec: Minimal shadows
    },
    VAppBar: {
      elevation: 0, // UX Spec: Minimal shadows
      flat: true,
    },
    VTextField: {
      rounded: 0,
      variant: 'outlined',
    },
    VSelect: {
      rounded: 0,
      variant: 'outlined',
    },
    VTextarea: {
      rounded: 0,
      variant: 'outlined',
    },
    VDialog: {
      rounded: 0,
    },
    VSheet: {
      rounded: 0,
    },
    VChip: {
      rounded: 0,
    },
  },
})
